import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'next-i18next'
import { graphqlAPI } from '../utils/graphql'
import { formatDate, formatCurrency, calculateEventPricing, isEventAvailable } from '../utils/helpers'
import { faCalendarAlt, faClock, faMapMarkerAlt } from '@fortawesome/free-solid-svg-icons';
import './EventListing.scss'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

const EventListing = () => {
  const { t } = useTranslation()
  const [events, setEvents] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    loadEvents()
  }, [])

  const loadEvents = async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await graphqlAPI.getPurchasableEvents()
      setEvents(data)
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const handleRetry = () => {
    loadEvents()
  }

  if (loading) {
    return (
      <div className="event-listing">
        <div className="container">
          <div className="text-center">
            <div className="spinner"></div>
            <p className="mt-3">{t('events.loading')}</p>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="event-listing">
        <div className="container">
          <div className="alert alert-danger text-center">
            <h4>{t('events.error')}</h4>
            <p>{error}</p>
            <button className="btn btn-primary" onClick={handleRetry}>
              {t('common.retry')}
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="event-listing">
      <div className="container">
        <div className="hero-section">
          <h1>{t('events.title')}</h1>
          <p className="lead text-center text-muted">
            {t('events.subtitle')}
          </p>
        </div>

        {events.length === 0 ? (
          <div className="no-events">
            <div className="text-center">
              <i className="fas fa-calendar-times fa-4x text-muted mb-4"></i>
              <h3>{t('events.noEvents')}</h3>
            </div>
          </div>
        ) : (
          <div className="events-grid">
            {events.map((event) => {
              const pricing = calculateEventPricing(event)
              const available = isEventAvailable(event)
              
              return (
                <EventCard
                  key={event.id}
                  event={event}
                  pricing={pricing}
                  available={available}
                  t={t}
                />
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}

const EventCard = ({ event, pricing, available, t }) => {
  const getActionLabel = () => {
    if (event.isSoldOut) {
      return t('events.soldOut')
    }

    if (!event.open) {
      return t('events.notOpen')
    }

    return t('events.unavailable')
  }

  return (
    <div className="event-card card">
      <div className="card-body">
        <div className="event-header">
           {pricing.isEarlyBird && (
            <div className="badge badge-early-bird">
              {t('events.earlyBird')}
            </div>
          )}
          <h3 className="card-title">{event.name}</h3>
         
        </div>

        <div className="event-details">
          <div className="detail-item">
            <FontAwesomeIcon icon={faCalendarAlt} className='fas fa-fw'/>
            <span>{formatDate(event.date)}</span>
          </div>
          
          {event.start_time && (
            <div className="detail-item">
              <FontAwesomeIcon icon={faClock} className='fas fa-fw'/>        
              <span>{event.start_time}</span>
            </div>
          )}
          
          {event.venue_name && (
            <div className="detail-item">
              <FontAwesomeIcon icon={faMapMarkerAlt} className='fas fa-fw'/>
              <span>{event.venue_name}</span>
            </div>
          )}
        </div>

        {event.summary && (
          <p className="event-summary">{event.summary}</p>
        )}

        <div className="event-pricing">
          <div className="price-info">
            <span className="price-label">
              {t('events.priceFrom', { price: formatCurrency(pricing.earlyPrice || pricing.currentPrice) })}
            </span>
            <span className="price-amount">
              {formatCurrency(pricing.currentPrice)}
            </span>
          </div>
          
          {pricing.isEarlyBird && pricing.latePrice > pricing.earlyPrice && (
            <div className="price-comparison">
              <small className="text-muted">
                {t('events.regular')}: {formatCurrency(pricing.latePrice)}
              </small>
            </div>
          )}
        </div>

        <div className="event-actions">
          {available ? (
            <Link
              to={`/event/${event.id}/tickets`}
              className="btn btn-primary btn-block"
            >
              {t('events.buyTickets')}
            </Link>
          ) : (
            <button className="btn btn-secondary btn-block" disabled>
              {getActionLabel()}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

export default EventListing
